import { normalizeUrl } from '@pkg/shared/utils/api.utils'
import { useMatches } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'

interface RouteNode {
  path?: string
  routes?: RouteNode[]
  children?: RouteNode[]
}

interface AntdMenuProps {
  openKeys?: string[]
  onOpenChange: (keys: string[]) => void
  onSelect: () => void
}

interface UseAntdProLayoutOptions {
  collapsed?: boolean
  routes?: RouteNode[]
}

interface AntdProLayoutProps {
  location: { pathname: string }
  selectedKeys?: string[]
  menuProps: AntdMenuProps
}

function collectPaths(nodes: RouteNode[] | undefined, acc: string[] = []): string[] {
  if (!nodes) return acc
  for (const node of nodes) {
    if (node.path?.startsWith('/')) acc.push(node.path)
    collectPaths(node.routes ?? node.children, acc)
  }
  return acc
}

function matchesPath(itemPath: string, pathname: string): boolean {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`)
}

export function useAntdProLayout(options: UseAntdProLayoutOptions = {}): AntdProLayoutProps {
  const { collapsed = false, routes } = options

  const pathname = useMatches({
    select: matches => {
      const last = matches[matches.length - 1]
      return normalizeUrl(last?.fullPath) ?? ''
    },
  })

  const [openKeys, setOpenKeys] = useState<string[] | undefined>(undefined)

  const onOpenChange = useCallback((keys: string[]) => {
    setOpenKeys(prev => {
      const added = keys.filter(k => !(prev ?? []).includes(k))
      return added.length > 0 ? added.slice(-1) : []
    })
  }, [])

  const onSelect = useCallback(() => {
    if (collapsed) setOpenKeys([])
  }, [collapsed])

  const selectedKeys = useMemo(() => {
    if (!routes) return undefined
    const candidates = collectPaths(routes)
    let best: string | undefined
    for (const candidate of candidates) {
      if (matchesPath(candidate, pathname) && (!best || candidate.length > best.length)) {
        best = candidate
      }
    }
    return best !== undefined ? [best] : []
  }, [routes, pathname])

  const menuProps = useMemo<AntdMenuProps>(
    () => ({
      ...(openKeys !== undefined ? { openKeys } : {}),
      onOpenChange,
      onSelect,
    }),
    [openKeys, onOpenChange, onSelect],
  )

  return useMemo<AntdProLayoutProps>(
    () => ({
      location: { pathname },
      selectedKeys,
      menuProps,
    }),
    [pathname, selectedKeys, menuProps],
  )
}
